/**
 * dogPose.ts — Pure mapping from the dog's state to how its body is posed.
 *
 * Today the dog's mood is only really legible from the HUD text. This gives each
 * state a distinct silhouette so you can read it from the dog itself:
 *
 *   CALM         upright, ears relaxed, easy wag
 *   DISTRACTED   ears perk up, leans toward whatever caught its eye, quicker wag
 *   HYPER_PULL   hard forward lean, ears pinned back, straining — the big yank
 *   SNIFFING     nose down, half crouched, casting about — needs to go soon
 *   POOPING      deep hunched squat, back rounded, ears down, no wag
 *   POOP_PENDING standing over it again, ears half down, a sheepish little wag
 *   CLEANED      upright and delighted, fast wag
 *
 * POOPING and POOP_PENDING are two different animals on purpose. The act now
 * takes a few seconds, and the only way to tell "it is going" from "it has
 * gone" at a glance is the silhouette: the squat is the lowest pose in the game
 * and the one after it stands back up.
 *
 * Renderer-free so it's unit-testable; `render/models.ts` applies the numbers.
 */
import { DogState } from '../types';

export interface DogPose {
  /** Forward pitch of the body, radians. Positive leans nose-down/forward. */
  lean: number;
  /** Ear rotation, radians. Positive = perked up, negative = pinned back. */
  earPerk: number;
  /** How far the body drops toward the ground, in world units. */
  crouch: number;
  /** Tail wag rate multiplier (0 = still). */
  wagSpeed: number;
  /** Tail wag amplitude multiplier. */
  wagAmount: number;
}

const POSES: Record<DogState, DogPose> = {
  [DogState.CALM]: { lean: 0, earPerk: 0, crouch: 0, wagSpeed: 1, wagAmount: 1 },
  [DogState.DISTRACTED]: { lean: 0.13, earPerk: 0.45, crouch: 0, wagSpeed: 1.7, wagAmount: 1.3 },
  [DogState.HYPER_PULL]: { lean: 0.34, earPerk: -0.5, crouch: 1.5, wagSpeed: 2.4, wagAmount: 0.6 },
  // Nose to the ground and half down on its haunches — the shape of a dog
  // hunting for a spot, and the warning that the squat is coming. Leaned
  // further than NEEDS_GRASS and crouched deeper, so the run-up to the urge
  // reads as its own thing rather than a milder version of it.
  [DogState.SNIFFING]: { lean: 0.24, earPerk: -0.2, crouch: 3, wagSpeed: 0.8, wagAmount: 0.5 },
  // Nose down and leaning into the lead: a dog looking for somewhere to go,
  // which is a different animal from one that has spotted a cat.
  [DogState.NEEDS_GRASS]: { lean: 0.2, earPerk: -0.15, crouch: 1, wagSpeed: 1.2, wagAmount: 0.7 },
  // Mid-act: hunched right down, tail still. The deepest crouch in the game, so
  // "the dog has stopped and is doing something" reads from across the street.
  [DogState.POOPING]: { lean: -0.3, earPerk: -0.45, crouch: 7, wagSpeed: 0, wagAmount: 0 },
  // Done: back on its feet beside the evidence, a little pleased with itself.
  [DogState.POOP_PENDING]: { lean: -0.05, earPerk: -0.1, crouch: 1, wagSpeed: 0.9, wagAmount: 0.5 },
  [DogState.CLEANED]: { lean: 0, earPerk: 0.25, crouch: 0, wagSpeed: 2.6, wagAmount: 1.5 },
};

/** The target pose for a state. Callers ease toward it rather than snapping. */
export function dogPoseForState(state: DogState): DogPose {
  return POSES[state];
}

/** Linear blend between two poses — used to ease between states. */
export function blendPose(from: DogPose, to: DogPose, t: number): DogPose {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  return {
    lean: from.lean + (to.lean - from.lean) * k,
    earPerk: from.earPerk + (to.earPerk - from.earPerk) * k,
    crouch: from.crouch + (to.crouch - from.crouch) * k,
    wagSpeed: from.wagSpeed + (to.wagSpeed - from.wagSpeed) * k,
    wagAmount: from.wagAmount + (to.wagAmount - from.wagAmount) * k,
  };
}
